import { useState } from "react";
import { useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { getStageCompletionStatusQueryOptions, useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { getFundingApplicationStagesQueryOptions } from "../queries/funding-application-configuration/queries";
import {
  isRejectedFundingApplicationStateTransition,
  useFundingApplicationStateMutation,
} from "../queries/funding-application/queries";
import { NAVIGATION_PATHS } from "../components/constants";
import type { PAGE_TYPE } from "./navigation-next";

export interface UseApplicationSubmitOptions {
  currentPage: PAGE_TYPE;
  currentStep?: string;
}

export function useApplicationSubmit({ currentPage, currentStep }: UseApplicationSubmitOptions) {
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: stageCompletionStatus } = useSuspenseQuery(getStageCompletionStatusQueryOptions(customerDomain));
  const { data: stagesConfig } = useSuspenseQuery(getFundingApplicationStagesQueryOptions(customerDomain));
  const setApplicationState = useFundingApplicationStateMutation(customerDomain);
  const { navigate } = useSpaNavigate("FundingApp");
  const posthog = usePostHog();
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const submitApplication = async () => {
    if (isModalOpen) {
      return;
    }
    const freshStatus = await queryClient
      .fetchQuery(getStageCompletionStatusQueryOptions(customerDomain))
      .catch(() => stageCompletionStatus);
    if (freshStatus.isSubmittable) {
      setApplicationState
        .mutateAsync({ applicationState: "submitted" })
        .then(() => {
          posthog?.capture("onboarding_submission_started", {
            outcome: "submitted",
            areStagesSatisfied: freshStatus,
          });
          navigate(NAVIGATION_PATHS.IN_REVIEW);
        })
        .catch((error) => {
          if (isRejectedFundingApplicationStateTransition(error)) {
            // Hook's onError has already redirected to root.
            return;
          }
          throw error;
        });
    } else {
      posthog?.capture("onboarding_submission_started", {
        outcome: "blocked",
        areStagesSatisfied: freshStatus,
      });
      setIsModalOpen(true);
    }
  };

  const closeModal = () => setIsModalOpen(false);

  return {
    isSubmitting: setApplicationState.isPending,
    isModalOpen,
    stageCompletionStatus,
    stagesConfig,
    currentPage,
    currentStep,
    submitApplication,
    closeModal,
    navigate,
  };
}
